import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiPlayCircle } = FiIcons;

const About = () => {
  const features = [
    'Experienced Professionals',
    'Advanced Medical Technology',
    'Emergency Help Available',
    'Quality Physiotherapy Service'
  ];

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <section id="about" className="py-16 lg:pt-40 lg:pb-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Image Composition */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              {/* Reliable Unsplash ID: Physio working on patient leg/knee */}
              <img 
                src="https://images.unsplash.com/photo-1666214280557-f1b5022eb634?auto=format&fit=crop&q=80&w=1000" 
                alt="Physiotherapy treatment in progress" 
                className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700" 
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500"></div>
            </div>
            {/* Experience Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute -bottom-4 -right-4 lg:-bottom-8 lg:-right-8 bg-emerald-600 text-white p-4 lg:p-8 rounded-xl shadow-xl hidden sm:block"
            >
              <div className="text-2xl lg:text-4xl font-bold mb-1">10+</div>
              <div className="text-xs lg:text-sm font-medium opacity-90">Years of<br/>Experience</div>
            </motion.div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="order-1 lg:order-2"
          >
            <motion.span variants={itemVariants} className="text-emerald-600 font-bold tracking-wider uppercase mb-2 block text-sm">
              About DIP Physiotherapy
            </motion.span>
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              We Specialize in <br/>
              <span className="text-teal-600">Physical Recovery</span>
            </motion.h2>
            <motion.p variants={itemVariants} className="text-base lg:text-lg text-slate-600 leading-relaxed mb-6">
              Our goal is to deliver quality of care in a courteous, respectful, and compassionate manner. We hope you will allow us to care for you and strive to be the first and best choice for your family healthcare.
            </motion.p>

            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-slate-700 font-medium text-sm lg:text-base">{feature}</span>
                </div>
              ))}
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-start sm:items-center gap-6 lg:gap-8">
              <a href="#contact" className="bg-slate-900 text-white px-8 py-4 rounded-lg font-bold hover:bg-emerald-600 transition-colors duration-300 w-full sm:w-auto text-center shadow-lg hover:shadow-emerald-500/20">
                Book Appointment
              </a>
              <div className="flex items-center gap-4">
                {/* Reliable Unsplash ID: Portrait of doctor */}
                <img 
                  src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&q=80&w=150" 
                  alt="Dipak Sahani" 
                  className="w-12 h-12 rounded-full object-cover border-2 border-emerald-100" 
                />
                <div>
                  <p className="text-sm text-slate-500">Head Physiotherapist</p>
                  <p className="font-bold text-slate-900">Dipak Sahani</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;